'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** The rate ladder Linear ships, in order. `>`/`<` step through this list. */
export const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

const IDLE_DELAY_MS = 2500;
const SEEK_STEP_SECONDS = 5;
const VOLUME_STEP = 0.1;

export type VideoPlayerStatus = 'playing' | 'paused' | 'ended';

/** MM:SS for the control-row readouts (marketing films are minutes, never hours). */
export const formatTimestamp = (seconds: number) => {
	const whole = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
	const m = String(Math.floor(whole / 60)).padStart(2, '0');
	const s = String(whole % 60).padStart(2, '0');
	return `${m}:${s}`;
};

/** "2 minutes 5 seconds" - what a screen reader should say, not what the eye reads. */
const spokenTimestamp = (seconds: number) => {
	const whole = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
	const m = Math.floor(whole / 60);
	const s = whole % 60;
	const parts: string[] = [];
	if (m > 0) parts.push(`${m} ${m === 1 ? 'minute' : 'minutes'}`);
	parts.push(`${s} ${s === 1 ? 'second' : 'seconds'}`);
	return parts.join(' ');
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** Fraction along a horizontal element, 0 at its left edge. */
const fractionFromPointerX = (element: HTMLElement, clientX: number) => {
	const rect = element.getBoundingClientRect();
	return rect.width === 0 ? 0 : clamp01((clientX - rect.left) / rect.width);
};

/** Fraction along a vertical element, 0 at its BOTTOM edge (volume grows upward). */
const fractionFromPointerY = (element: HTMLElement, clientY: number) => {
	const rect = element.getBoundingClientRect();
	return rect.height === 0 ? 0 : clamp01((rect.bottom - clientY) / rect.height);
};

interface UseVideoPlayerOptions {
	/** When set, the caption track is parsed by the browser and painted by us. */
	captionsSrc?: string;
	/** Mirrors the `<video autoplay>` attribute - seeds the OPTIMISTIC status. */
	autoPlay?: boolean;
	/**
	 * Initial muted state. Only the STARTING value - the mute button, `m`, and the
	 * volume slider all keep working, and the volume UI reflects it from the start.
	 * An autoplaying player must pass `true`: browsers refuse an audible autoplay.
	 */
	muted?: boolean;
	/** Start with captions showing (only meaningful with captionsSrc). */
	defaultCaptionsOn?: boolean;
}

/**
 * All of the media wiring behind `VideoPlayer` - playback, volume,
 * scrubbing, captions, fullscreen, PiP, the idle timer and the keyboard map.
 * The component that consumes this owns only markup and class names.
 */
export const useVideoPlayer = ({
	captionsSrc,
	autoPlay = false,
	muted: initialMuted = false,
	defaultCaptionsOn = false,
}: UseVideoPlayerOptions) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const trackRef = useRef<HTMLTrackElement>(null);

	// Autoplay is a REQUEST, not a fact - the browser may refuse it. Seeding
	// `playing` only avoids a frame of "paused" chrome; the play/pause/ended
	// listeners and the mount-time play() below make the status truthful.
	const [status, setStatus] = useState<VideoPlayerStatus>(autoPlay ? 'playing' : 'paused');
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	/** The file's own pixel size once metadata is in - drives `aspect="native"`. */
	const [intrinsic, setIntrinsic] = useState<{ width: number; height: number } | null>(null);
	const [buffered, setBuffered] = useState({ start: 0, end: 0 });
	const [volume, setVolumeState] = useState(1);
	const [muted, setMuted] = useState(initialMuted);
	const [playbackRate, setPlaybackRateState] = useState(1);
	const [captionsOn, setCaptionsOn] = useState(defaultCaptionsOn);
	const [captionText, setCaptionText] = useState('');
	const [idle, setIdle] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [pipSupported, setPipSupported] = useState(false);
	const [scrubFraction, setScrubFraction] = useState<number | null>(null);
	const [volumeInteracting, setVolumeInteracting] = useState(false);

	// The volume to restore when unmuting - remembered from the last audible level.
	const lastVolumeRef = useRef(1);
	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const seekingRef = useRef(false);

	// PiP support is a client-only fact; reading it during render would desync
	// the server markup, so the button mounts after hydration.
	useEffect(() => {
		setPipSupported(typeof document !== 'undefined' && Boolean(document.pictureInPictureEnabled));
	}, []);

	// React does not reliably emit the `muted` ATTRIBUTE in server markup, so the
	// initial muted state is applied as a PROPERTY here. It has to land before the
	// autoplay effect below asks to play - every browser refuses an audible
	// autoplay, and a refused play() would drop the player straight to `paused`.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		video.muted = initialMuted;
	}, [initialMuted]);

	// Autoplay is seeded optimistically as `playing` so the chrome doesn't flash
	// its paused state for a frame. The attribute alone gives us no signal when
	// the browser REFUSES (no `pause` event fires for a play that never started),
	// so we also ask explicitly and fall back to `paused` on the rejection. The
	// `play`/`pause`/`ended` handlers on the element own the truth from then on.
	useEffect(() => {
		if (!autoPlay) return;
		const video = videoRef.current;
		if (!video) return;
		void video.play().catch(() => setStatus('paused'));
	}, [autoPlay]);

	/* ---------------------------------------------------------------- playback */

	const play = useCallback(() => {
		void videoRef.current?.play().catch(() => undefined);
	}, []);

	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused || video.ended) play();
		else video.pause();
	}, [play]);

	const seekTo = useCallback((seconds: number) => {
		const video = videoRef.current;
		if (!video || !Number.isFinite(video.duration)) return;
		const next = Math.min(video.duration, Math.max(0, seconds));
		video.currentTime = next;
		setCurrentTime(next);
	}, []);

	const seekByFraction = useCallback(
		(fraction: number) => {
			const video = videoRef.current;
			if (!video || !Number.isFinite(video.duration)) return;
			seekTo(clamp01(fraction) * video.duration);
		},
		[seekTo],
	);

	const stepSeek = useCallback(
		(direction: 1 | -1) => {
			const video = videoRef.current;
			if (!video) return;
			seekTo(video.currentTime + direction * SEEK_STEP_SECONDS);
		},
		[seekTo],
	);

	/* ------------------------------------------------------------------ volume */

	const applyVolume = useCallback((next: number) => {
		const video = videoRef.current;
		const clamped = clamp01(next);
		setVolumeState(clamped);
		if (clamped > 0) lastVolumeRef.current = clamped;
		if (video) {
			video.volume = clamped;
			// Dragging the slider up is an implicit unmute.
			if (clamped > 0 && video.muted) {
				video.muted = false;
				setMuted(false);
			}
		}
	}, []);

	const stepVolume = useCallback(
		(direction: 1 | -1) => applyVolume(volume + direction * VOLUME_STEP),
		[applyVolume, volume],
	);

	const toggleMute = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		const nextMuted = !video.muted;
		video.muted = nextMuted;
		setMuted(nextMuted);
		if (!nextMuted && video.volume === 0) applyVolume(lastVolumeRef.current || 1);
	}, [applyVolume]);

	/* ------------------------------------------------------------------- rates */

	const setPlaybackRate = useCallback((rate: number) => {
		setPlaybackRateState(rate);
		if (videoRef.current) videoRef.current.playbackRate = rate;
	}, []);

	const stepPlaybackRate = useCallback(
		(direction: 1 | -1) => {
			const index = PLAYBACK_RATES.indexOf(playbackRate as (typeof PLAYBACK_RATES)[number]);
			const from = index === -1 ? PLAYBACK_RATES.indexOf(1) : index;
			const next = Math.min(PLAYBACK_RATES.length - 1, Math.max(0, from + direction));
			setPlaybackRate(PLAYBACK_RATES[next]);
		},
		[playbackRate, setPlaybackRate],
	);

	/* ---------------------------------------------------------------- captions */

	const toggleCaptions = useCallback(() => setCaptionsOn((on) => !on), []);

	// The <track> stays `hidden` rather than `showing`: the browser still parses
	// the VTT and fires cuechange, but paints nothing - we paint the placard so it
	// matches the rest of the chrome.
	useEffect(() => {
		const track = trackRef.current?.track;
		if (!track) return;
		track.mode = captionsOn ? 'hidden' : 'disabled';
		if (!captionsOn) {
			setCaptionText('');
			return;
		}
		const readCues = () => {
			const cues = Array.from(track.activeCues ?? []);
			const text = cues
				.map((cue) => ('text' in cue ? String((cue as VTTCue).text) : ''))
				.join(' ')
				.replace(/<[^>]+>/g, '')
				.trim();
			setCaptionText(text);
		};
		readCues();
		track.addEventListener('cuechange', readCues);
		return () => track.removeEventListener('cuechange', readCues);
	}, [captionsOn, captionsSrc]);

	/* ------------------------------------------------------- fullscreen and PiP */

	const toggleFullscreen = useCallback(() => {
		const root = rootRef.current;
		const video = videoRef.current as
			| (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
			| null;
		if (document.fullscreenElement) {
			void document.exitFullscreen().catch(() => undefined);
			return;
		}
		// Fullscreen goes on the ROOT so our chrome comes with it. iOS Safari has
		// no element fullscreen, so the video's own native presentation stands in.
		if (root?.requestFullscreen) {
			void root.requestFullscreen().catch(() => undefined);
			return;
		}
		video?.webkitEnterFullscreen?.();
	}, []);

	const togglePip = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (document.pictureInPictureElement) {
			void document.exitPictureInPicture().catch(() => undefined);
			return;
		}
		void video.requestPictureInPicture?.().catch(() => undefined);
	}, []);

	useEffect(() => {
		const onChange = () => setFullscreen(document.fullscreenElement === rootRef.current);
		document.addEventListener('fullscreenchange', onChange);
		return () => document.removeEventListener('fullscreenchange', onChange);
	}, []);

	/* -------------------------------------------------------------- idle timer */

	const markActive = useCallback(() => {
		setIdle(false);
		if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		// Only a PLAYING player goes idle - a paused one keeps its chrome up.
		if (videoRef.current?.paused !== false) return;
		idleTimerRef.current = setTimeout(() => setIdle(true), IDLE_DELAY_MS);
	}, []);

	useEffect(() => {
		if (status !== 'playing') {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
			setIdle(false);
			return;
		}
		markActive();
		return () => {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		};
	}, [markActive, status]);

	/* --------------------------------------------------------- media listeners */

	const syncBuffered = useCallback(() => {
		const video = videoRef.current;
		if (!video || !Number.isFinite(video.duration) || video.duration === 0) return;
		const ranges = video.buffered;
		for (let i = 0; i < ranges.length; i += 1) {
			if (ranges.start(i) <= video.currentTime && ranges.end(i) >= video.currentTime) {
				setBuffered({
					start: (ranges.start(i) / video.duration) * 100,
					end: (ranges.end(i) / video.duration) * 100,
				});
				return;
			}
		}
	}, []);

	const handleTimeUpdate = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		setCurrentTime(video.currentTime);
		syncBuffered();
	}, [syncBuffered]);

	const handleLoadedMetadata = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		setDuration(Number.isFinite(video.duration) ? video.duration : 0);
		if (video.videoWidth > 0 && video.videoHeight > 0) {
			setIntrinsic({ width: video.videoWidth, height: video.videoHeight });
		}
		video.volume = volume;
		video.playbackRate = playbackRate;
		setMuted(video.muted);
	}, [playbackRate, volume]);

	/* ------------------------------------------------------- pointer scrubbing */

	const handleProgressPointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			event.currentTarget.setPointerCapture(event.pointerId);
			seekingRef.current = true;
			const fraction = fractionFromPointerX(event.currentTarget, event.clientX);
			setScrubFraction(fraction);
			seekByFraction(fraction);
		},
		[seekByFraction],
	);

	const handleProgressPointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const fraction = fractionFromPointerX(event.currentTarget, event.clientX);
			// Hovering only moves the preview thumb; dragging also moves the film.
			setScrubFraction(fraction);
			if (seekingRef.current) seekByFraction(fraction);
		},
		[seekByFraction],
	);

	const handleProgressPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		seekingRef.current = false;
	}, []);

	const handleProgressPointerLeave = useCallback(() => {
		if (!seekingRef.current) setScrubFraction(null);
	}, []);

	const handleVolumePointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			event.currentTarget.setPointerCapture(event.pointerId);
			setVolumeInteracting(true);
			applyVolume(fractionFromPointerY(event.currentTarget, event.clientY));
		},
		[applyVolume],
	);

	const handleVolumePointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!volumeInteracting) return;
			applyVolume(fractionFromPointerY(event.currentTarget, event.clientY));
		},
		[applyVolume, volumeInteracting],
	);

	const handleVolumePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		setVolumeInteracting(false);
	}, []);

	/* ---------------------------------------------------------------- keyboard */

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			const target = event.target as HTMLElement | null;
			const isNativeControl =
				target instanceof HTMLSelectElement || target instanceof HTMLButtonElement;
			// Let a focused button/select handle its own Enter/Space/typing.
			if (isNativeControl && (event.key === ' ' || event.key === 'Enter')) return;
			if (target instanceof HTMLSelectElement) return;

			switch (event.key) {
				case 'k':
				case ' ':
					event.preventDefault();
					togglePlay();
					break;
				case 'm':
					event.preventDefault();
					toggleMute();
					break;
				case 'f':
					event.preventDefault();
					toggleFullscreen();
					break;
				case 'p':
					if (!pipSupported) return;
					event.preventDefault();
					togglePip();
					break;
				case 'c':
					if (!captionsSrc) return;
					event.preventDefault();
					toggleCaptions();
					break;
				case '>':
				case '.':
					if (!event.shiftKey) return;
					event.preventDefault();
					stepPlaybackRate(1);
					break;
				case '<':
				case ',':
					if (!event.shiftKey) return;
					event.preventDefault();
					stepPlaybackRate(-1);
					break;
				case 'ArrowLeft':
					event.preventDefault();
					stepSeek(-1);
					break;
				case 'ArrowRight':
					event.preventDefault();
					stepSeek(1);
					break;
				case 'ArrowUp':
					event.preventDefault();
					stepVolume(1);
					break;
				case 'ArrowDown':
					event.preventDefault();
					stepVolume(-1);
					break;
				default:
					return;
			}
			markActive();
		},
		[
			captionsSrc,
			markActive,
			pipSupported,
			stepPlaybackRate,
			stepSeek,
			stepVolume,
			toggleCaptions,
			toggleFullscreen,
			toggleMute,
			togglePip,
			togglePlay,
		],
	);

	/* ------------------------------------------------------------- derived bits */

	const playedPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
	const previewPercent = (scrubFraction ?? 0) * 100;
	const previewSeconds = (scrubFraction ?? 0) * duration;

	const seekValueText = useMemo(
		() => `${spokenTimestamp(currentTime)} of ${spokenTimestamp(duration)}`,
		[currentTime, duration],
	);

	return {
		refs: { rootRef, videoRef, trackRef },
		state: {
			intrinsic,
			status,
			idle,
			currentTime,
			duration,
			buffered,
			volume,
			muted,
			playbackRate,
			captionsOn,
			captionText,
			fullscreen,
			pipSupported,
			volumeInteracting,
			scrubFraction,
			playedPercent,
			previewPercent,
			previewSeconds,
			seekValueText,
		},
		actions: {
			togglePlay,
			toggleMute,
			toggleCaptions,
			toggleFullscreen,
			togglePip,
			setPlaybackRate,
			markActive,
			handleKeyDown,
			handleTimeUpdate,
			handleLoadedMetadata,
			syncBuffered,
			setStatus,
			handleProgressPointerDown,
			handleProgressPointerMove,
			handleProgressPointerUp,
			handleProgressPointerLeave,
			handleVolumePointerDown,
			handleVolumePointerMove,
			handleVolumePointerUp,
		},
	};
};
